import { Component, Input, Output, EventEmitter, output, input, inject, computed } from '@angular/core';
import {
  ConfigurationService,
  DisabledEngravingModalData,
  EngravingModalService,
  EngravingUtilitiesService,
  ProcessedProduct,
} from '@core';
import { AppleCareModalService } from '@core';
import type { ProductCart } from '@models';
import { usdToPoints, formatRewardAmount } from '@models';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { CartOldPriceComponent } from '../../../shared/src/lib/cart-old-price/cart-old-price.component';
import { AlertMessageComponent } from '../../../shared/src/lib/alert-message/alert-message.component';
import { CounterComponent } from '../../../shared/src/lib/counter/counter.component';
import { LightCarouselComponent } from '@light-carousel';

export interface AppleCareItem {
  id: string;
  productName: string;
  price: number;
  points?: number;
  isAdded: boolean;
  brand: string;
  model: string;
  productId: number;
  lineNumber?: number;
}
@Component({
  selector: 'app-apple-care-listing-product',
  templateUrl: './apple-care-listing-product.component.html',
  styleUrls: ['./apple-care-listing-product.component.scss'],
  imports: [
    CommonModule,
    AlertMessageComponent,
    CartOldPriceComponent,
    CounterComponent,
    MatIcon,
    LightCarouselComponent,
  ],
})
export class AppleCareListingProductComponent {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  // --- AppleCare Inputs/Outputs (Restored) ---
  @Input() appleCareItem: AppleCareItem | null = null;
  @Input() showControls = true;
  @Input() isOrderReview = false;
  @Input() isMiniCart = false;
  // Outputs
  @Output() addAppleCare = new EventEmitter<{ appleCareItem: AppleCareItem; parentLineNumber: number }>();
  @Output() removeAppleCare = new EventEmitter<{ appleCareItem: AppleCareItem; parentLineNumber: number }>();
  @Output() deleteEngraving = new EventEmitter<{ productId: number }>();
  @Output() remove = new EventEmitter<{ productId: number }>();

  // --- Quantity Restriction Inputs ---
  @Input() disableAppleCare = false;
  @Input() disableEngraving = false;

  /**
   * FCR 19.1 — optional monthly-limit warning shown under the card (AC05b), same placement as the
   * merch tile (`lib-cart-item`). Since RCP-452 the cart hosts render Apple via `lib-cart-item`.
   * The consolidated AC07b removal message is a separate, cart-level banner
   * (`lib-cart-warning-banners`). Empty/undefined hides it.
   */
  @Input() limitMessage?: string;

  appleCareLoading = input<boolean>(false);
  hasAppleCareAdded = computed(() => this.appleCareItem?.isAdded ?? false);

  vm = computed(() => {
    const product = this.product();
    const price = product?.price ?? 0;
    const discountedPrice = product?.discountedPrice ?? 0;

    const pricePoints = usdToPoints(price);
    const discountedPricePoints = discountedPrice ? usdToPoints(discountedPrice) : 0;

    const isPromo = product?.isPromo;

    let displayNewPoints: number;
    let displayOldPoints: number | null;
    let displayNewPrice: number;
    let displayOldPrice: number | null;

    if (isPromo && discountedPrice) {
      // Price discount: show discounted price/points, old regular price/points
      displayNewPoints = discountedPricePoints;
      displayNewPrice = discountedPrice;
      displayOldPrice = price;
      displayOldPoints = pricePoints;
    } else {
      // No price discount
      displayNewPoints = pricePoints;
      displayNewPrice = price;
      displayOldPrice = null;
      displayOldPoints = null;
    }

    return {
      product,
      points: pricePoints,
      discountedPoints: discountedPricePoints,
      smallImagesForCards: product?.images,
      // New display values
      displayNewPoints,
      displayOldPoints,
      displayNewPrice,
      displayOldPrice,
      shouldShowOldPoints: displayOldPoints !== null,
    };
  });

  engravingLines = computed(() => {
    const text = this.engravingText();
    if (!text) {
      return [];
    }
    return text.split('\n');
  });

  // --- Core Inputs ---
  product = input<ProductCart | ProcessedProduct>();
  loading = input<boolean>();
  mode = input<'mobile' | 'tablet'>();

  hasAppleCare = input<boolean>(false);
  isEngravable = input<boolean>(false);
  engravingText = input<string | null>();

  editEngraving = output<void>();

  // Services
  private engravingModalService = inject(EngravingModalService);
  private engravingService = inject(EngravingUtilitiesService);
  private appleCareModalService = inject(AppleCareModalService);

  onRemove(id: number): void {
    this.remove.emit({ productId: id });
  }

  onEditEngraving(): void {
    if (this.disableEngraving) {
      return;
    }
    this.editEngraving.emit();
  }

  onDeleteEngraving(): void {
    const prod = this.product();
    if (!prod) {
      return;
    }
    this.deleteEngraving.emit({ productId: prod.productId });
  }

  /**
   * Add AppleCare - always adds exactly 1
   */
  onAddAppleCare(): void {
    if (this.disableAppleCare || this.isAppleCareActionDisabled()) {
      return;
    }
    if (this.appleCareItem) {
      const parentProduct = this.product();
      // validate that lineNumber is a positive number
      if (!parentProduct?.lineNumber || parentProduct.lineNumber <= 0) {
        console.error('Cannot add AppleCare: Parent product lineNumber is missing or invalid', {
          parentProduct,
          lineNumber: parentProduct?.lineNumber,
        });
        return;
      }

      // Emit both the AppleCare item and the parent's lineNumber (validated above)
      this.addAppleCare.emit({
        appleCareItem: this.appleCareItem,
        parentLineNumber: parentProduct.lineNumber,
      });
    }
  }

  /**
   * Remove AppleCare - removes the 1:1 linked item
   */
  onRemoveAppleCare(): void {
    if (this.appleCareItem && !this.isAppleCareActionDisabled()) {
      const parentProduct = this.product();
      if (!parentProduct?.lineNumber) {
        console.error('Parent product lineNumber is missing');
        return;
      }
      this.removeAppleCare.emit({
        appleCareItem: this.appleCareItem,
        parentLineNumber: parentProduct.lineNumber,
      });
    }
  }

  onLearnMoreAppleCare(): void {
    if (!this.appleCareItem) {
      return;
    }

    try {
      void this.appleCareModalService
        .fetchAppleCareData(`${this.appleCareItem.brand} ${this.appleCareItem.model}`)
        .then((modalData) => {
          this.appleCareModalService['modalDataSubject'].next(modalData);
          this.appleCareModalService['isModalOpenSubject'].next(true);
        });
    } catch (error) {
      console.error('Failed to load AppleCare information:', error);
    }
  }

  // Disabled state checks
  isAppleCareActionDisabled(): boolean {
    return this.appleCareLoading() || this.loading() || this.disableAppleCare;
  }

  isEngravingActionDisabled(): boolean {
    return this.loading() || this.disableEngraving;
  }

  shouldDisableQuantityControls(): boolean {
    // Quantity controls for the main product, not AppleCare
    return false;
  }

  // Display helpers
  // Prices are loaded in Shopping Dollars; shown as whole numbers with the reward name.
  formatPrice(price: number | null | undefined): string {
    return formatRewardAmount(price, this.rewardName());
  }

  /**
   * SIMPLIFIED: Button text for 1:1 AppleCare
   */
  getAppleCareButtonText(): string {
    if (this.isAppleCareActionDisabled()) {
      if (this.disableAppleCare) {
        return 'Not available (qty > 1)';
      }
      return 'Processing...';
    }

    // Simple: Either add or it's already added
    return this.hasAppleCareAdded() ? 'AppleCare+ Added' : 'Add AppleCare+';
  }

  getEngravingButtonText(): string {
    if (this.isEngravingActionDisabled()) {
      if (this.disableEngraving) {
        return 'Not available (qty > 1)';
      }
      return 'Loading...';
    }
    return this.engravingText() ? 'Edit' : 'Add';
  }

  // Section visibility
  shouldShowAppleCareSection(): boolean {
    return !!this.appleCareItem && (!this.disableAppleCare || !!this.appleCareItem.isAdded);
  }

  shouldShowEngravingSection(): boolean {
    return this.isEngravable() && !this.disableEngraving;
  }

  shouldShowAddEngravingSection(): boolean {
    return this.isEngravable() && !this.engravingText() && !this.disableEngraving;
  }

  // Type guards
  isProcessedProduct(product: ProductCart | ProcessedProduct): product is ProcessedProduct {
    return 'engravingText' in product && 'isEngravable' in product;
  }

  hasEngraving(product: ProductCart | ProcessedProduct): boolean {
    if (this.isProcessedProduct(product)) {
      return this.engravingService.hasEngraving(product);
    }
    return false;
  }

  viewEngravingDetails(product: ProductCart | ProcessedProduct): void {
    if (!this.isProcessedProduct(product)) {
      return;
    }
    if (!product.engravingText) {
      return;
    }

    const disabledModalData: DisabledEngravingModalData = {
      product: product,
      engravingText: product.engravingText,
    };

    this.engravingModalService.openDisabledModal(disabledModalData);
  }
}
